import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import StatusChange from './StatusChange';
import { findAndMap } from './S4_2_1_StatusBulkChange';
import { useNavigate } from 'react-router-dom';
import useWaWorkplaceService from 'src/api/wa/service/WaWorkplaceService';
import Button from 'src/ui/wards/common/button/Button';
import {
  RequestDetailsOption,
  RequestDetailsOptionFactory,
} from 'src/api/wa/store/models/statusChange/StatusChange';
import { getDetailStatusTranslationKey } from 'src/api/wa/store/models/workplace/OperationStatus';
import { useWaProductStore } from 'src/api/wa/store/WaProductStore';
import { useWaWorkplaceStore } from 'src/api/wa/store/WaWorkplaceStore';
import { useWaLocationService } from 'src/api/wa/service/WaLocationService';
import { useWaDisasterStore } from 'src/api/wa/store/WaRegionAndDisasterStore';
import { useNotify } from 'src/ui/common/alert/useNotify';
import { useUserAgent } from 'src/common/userAgent/useUserAgent';

export default function S4_3_1_EditProduct() {
  const { t } = useTranslation();
  const { selectedWorkplace } = useWaWorkplaceStore();
  const { selectedProduct, setSelectedProduct } = useWaProductStore();
  const { updateProductStatus, reloadhWorkplace } = useWaWorkplaceService();
  const { selectedDisaster } = useWaDisasterStore();
  const navigate = useNavigate();
  const notify = useNotify();
  const userAgent = useUserAgent();

  const currentProductStatusId = selectedProduct?.statusId ?? '00';
  const statusOptions = RequestDetailsOptionFactory.generateSet(currentProductStatusId);

  const getDefaultStatus = (id: string): string => {
    if (id === '65') return '26';

    const recoveryStatuses = ['41', '80', '46', '49', '45', '28', '54', '43'];
    if (recoveryStatuses.includes(id)) return '22';

    return '01';
  };

  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<string>(getDefaultStatus(currentProductStatusId));
  const { updateWaUserLocation } = useWaLocationService();

  const handleUpdateStatus = async () => {
    if (!selectedProduct || !selectedWorkplace) {
      navigate(-1);
      return;
    }

    if (isLoading) return;
    setIsLoading(true);

    try {
      const res = await updateProductStatus(
        selectedWorkplace.customerId,
        selectedWorkplace.branchCode,
        [selectedProduct.productNumber],
        status
      );

      if (!res) {
        notify('wards.common.updateError');
        setIsLoading(false);
        return;
      }

      // DB更新が確定した場合のみストアの表示用ステータスを反映する
      setSelectedProduct({
        ...selectedProduct,
        originalStatusId: status,
        statusId: status,
      });

      if (userAgent.isMobile) {
        updateWaUserLocation();
      }

      notify('wards.common.updateSuccess', () => {
        navigate(-1);
      });
    } catch (error) {
      notify('wards.common.updateError');
      console.error(error);
      setIsLoading(false);
    } finally {
      reloadhWorkplace(
        selectedDisaster?.id ? selectedDisaster.id : '',
        selectedWorkplace?.branchCode,
        selectedWorkplace?.customerId
      );
    }
  };

  const handleCancelAction = () => {
    navigate(-1);
  };

  const backNavigation = () => {
    navigate(-1);
  };

  return (
    <div className="flex min-h-screen flex-col space-y-2 bg-gray-100 px-4 py-8">
      <div className="mx-auto w-full max-w-xl px-4">
        <h2 className="text-left text-2xl font-bold">{t('wards.productChange.title')}</h2>
        <div className="flex justify-start pt-4">
          <Button title={t('wards.common.back')} onClick={backNavigation} />
        </div>
        <StatusChange
          status={findAndMap(
            statusOptions.list,
            (u) => u === status,
            (u) => ({
              placeholderTranslationKey: RequestDetailsOptionFactory.getOptionTranslationKey(u),
              value: String(u),
            })
          )}
          statusList={statusOptions.list
            .filter((item) => item !== RequestDetailsOption.updateOnlyComment)
            .map((item) => ({
              placeholderTranslationKey: RequestDetailsOptionFactory.getOptionTranslationKey(item),
              value: String(item),
            }))}
          setStatus={(item) => setStatus(item.value as string)}
          isLoading={isLoading}
          handleOkAction={handleUpdateStatus}
          handleCancelAction={handleCancelAction}
          bulletPoints={[
            {
              placeholderTranslationKey: 'wards.common.status',
              value: selectedProduct?.statusId
                ? t(getDetailStatusTranslationKey(selectedProduct?.statusId))
                : '',
            },
            {
              placeholderTranslationKey: 'wards.targetProductList.productNumber',
              value: `${selectedProduct?.productNumber}`,
            },
            {
              placeholderTranslationKey: 'wards.desktop.unaddressedPropertiesList.comment',
              value: selectedWorkplace?.details?.comment ?? '',
            },
          ]}
        />
      </div>
    </div>
  );
}
